import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Typography,
  Space,
  Tag,
  Empty,
  Spin,
  Alert,
  Button,
  Modal,
  Select,
  Input,
  message,
  Tooltip,
} from 'antd';
import SegmentAudioPlayer from '../components/SegmentAudioPlayer';
import AudioTranscriptionEditor from '../components/AudioTranscriptionEditor';
import { useDevice } from '../contexts/DeviceContext';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { formatJstDateTime } from '../utils/dateTime';
import {
  getWhisperSegmentSummary,
  getWhisperTranscriptMessage,
  hasRecognizedSpeech,
  isNoSpeechDetected,
  isWhisperBatchEngine,
} from '../utils/whisperTranscriptState';
import {
  TranscriptDetail,
  TranscriptListResponse,
  TranscriptSummary,
  TranscriptSegment,
  PersonSummary,
} from '../types/api';

const { Title, Text } = Typography;

interface AudioSnapshot {
  id: string;
  start_time: string;
  received_at: string;
  duration_ms: number;
  codec: string;
  sampling_rate: number;
  bit_depth: number;
  bitrate: number;
  channels: number;
  file_size: number;
  status: string;
  transcription?: string;
  confidence?: number;
  latitude?: number;
  longitude?: number;
}

interface TranscriptDataEntry {
  summary?: TranscriptSummary;
  detail?: TranscriptDetail;
}

type NormalizedWord = {
  key: string;
  text: string;
  startRelSec: number;
  endRelSec: number;
  startAbsSec: number;
  endAbsSec: number;
};

type NormalizedSegment = {
  key: string;
  lookupKey: string;
  transcriptSegmentId?: number;
  startMs: number;
  endMs: number;
  startSec: number;
  endSec: number;
  text?: string;
  speaker?: string;
  speakerConf?: number;
  words?: NormalizedWord[];
};

type AudioEnrollmentSegmentState = {
  id: string;
  person_id: string;
  person_display_name?: string | null;
  transcript_segment_id?: number | null;
  start_ms: number;
  end_ms: number;
  duration_ms: number;
   enrollment_state?: string;
};

const pickPreferredTranscript = (transcripts: TranscriptSummary[]): TranscriptSummary | undefined => {
  if (!transcripts?.length) {
    return undefined;
  }
  return transcripts.find((t) => isWhisperBatchEngine(t.engine)) ?? transcripts[0];
};

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return '--:--';
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(Math.floor(seconds % 60)).padStart(2, '0');
  return `${mm}:${ss}`;
};

const buildLookupKey = (segmentId?: number, startMs?: number, endMs?: number) => {
  if (typeof segmentId === 'number' && Number.isFinite(segmentId)) {
    return `segment-${segmentId}`;
  }
  if (
    typeof startMs === 'number' &&
    Number.isFinite(startMs) &&
    typeof endMs === 'number' &&
    Number.isFinite(endMs)
  ) {
    return `range-${startMs}-${endMs}`;
  }
  return '';
};

const describeEnrollmentState = (state?: string) => {
  switch ((state || 'pending').toLowerCase()) {
    case 'enrolled':
      return { label: '登録済み', color: 'cyan' as const };
    case 'failed':
      return { label: '失敗', color: 'red' as const };
    case 'queued':
      return { label: '投入済み', color: 'blue' as const };
    default:
      return { label: '未登録', color: 'default' as const };
  }
};

const buildSegmentsForAudio = (detail: TranscriptDetail | undefined, audioId: string): NormalizedSegment[] => {
  if (!detail?.segments?.length) {
    return [];
  }

  return detail.segments
    .map<NormalizedSegment | null>((segment: TranscriptSegment, idx: number) => {
      const slice = segment.slices?.find((s) => s.audio_id === audioId);
      const startMs = slice?.start_offset_ms ?? segment.start_ms;
      const endMs = slice?.end_offset_ms ?? segment.end_ms;
      if (startMs == null || endMs == null) {
        return null;
      }
      const numericId =
        typeof segment.id === 'number'
          ? segment.id
          : segment.id != null && !Number.isNaN(Number(segment.id))
          ? Number(segment.id)
          : undefined;
      const startSec = Math.max(0, startMs / 1000);
      const endSec = Math.max(startSec, endMs / 1000);
      const words =
        segment.words
          ?.map<NormalizedWord | null>((word, wIdx) => {
            const rawStart =
              (word as any).start_ms ?? (word as any).start_time ?? (word as any).start ?? null;
            const rawEnd =
              (word as any).end_ms ?? (word as any).end_time ?? (word as any).end ?? null;
            const wordStartMs = typeof rawStart === 'number' ? rawStart : Number(rawStart);
            const wordEndMs = typeof rawEnd === 'number' ? rawEnd : Number(rawEnd);
            if (!Number.isFinite(wordStartMs) || !Number.isFinite(wordEndMs)) {
              return null;
            }
            const startRelSec = Math.max(0, (wordStartMs - startMs) / 1000);
            const endRelSec = Math.max(startRelSec, (wordEndMs - startMs) / 1000);
            const rawText =
              (word as any).text ?? (word as any).word ?? (word as any).token ?? '(空白)';
            const text = typeof rawText === 'string' ? rawText.trim() : String(rawText);
            return {
              key: `${segment.id ?? audioId}-word-${wIdx}`,
              text: text || '(空白)',
              startRelSec,
              endRelSec,
              startAbsSec: wordStartMs / 1000,
              endAbsSec: wordEndMs / 1000,
            };
          })
          .filter((w): w is NormalizedWord => Boolean(w)) || [];

      return {
        key: `${segment.id ?? audioId}-${idx}`,
        lookupKey: buildLookupKey(numericId, startMs, endMs),
        transcriptSegmentId: numericId,
        startMs,
        endMs,
        startSec,
        endSec,
        text: segment.text,
        speaker: segment.speaker_label,
        speakerConf: segment.speaker_confidence ?? segment.segment_confidence,
        words,
      };
    })
    .filter((seg): seg is NormalizedSegment => Boolean(seg))
    .sort((a, b) => a.startSec - b.startSec);
};

const parseTimestamp = (value?: string) => {
  if (!value) return null;
  const normalized = value.match(/(Z|[+-]\d{2}:?\d{2})$/) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

type AudioDetailPageProps = {
  audioIdOverride?: string;
  embedded?: boolean;
  initialSeekTimestamp?: string | null;
  onClose?: () => void;
};

const AudioDetailPage: React.FC<AudioDetailPageProps> = ({
  audioIdOverride,
  embedded = false,
  initialSeekTimestamp = null,
  onClose,
}) => {
  const { audioId: paramAudioId } = useParams<{ audioId: string }>();
  const audioId = audioIdOverride ?? paramAudioId;
  const { selectedDevice } = useDevice();
  const [audioData, setAudioData] = useState<AudioSnapshot | null>(null);
  const [transcriptEntry, setTranscriptEntry] = useState<TranscriptDataEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [personList, setPersonList] = useState<PersonSummary[]>([]);
  const [personListLoading, setPersonListLoading] = useState(false);
  const [enrollmentSegments, setEnrollmentSegments] = useState<AudioEnrollmentSegmentState[]>([]);
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [assignTargetSegment, setAssignTargetSegment] = useState<NormalizedSegment | null>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<string | undefined>();
  const [assignNotes, setAssignNotes] = useState('');
  const [assigningKey, setAssigningKey] = useState<string | null>(null);
  const [removingEnrollmentId, setRemovingEnrollmentId] = useState<string | null>(null);
  const [retryingWhisper, setRetryingWhisper] = useState(false);
  const [pendingInitialSeek, setPendingInitialSeek] = useState<string | null>(initialSeekTimestamp);

  const renderStatusTag = useCallback((status?: string) => {
    if (!status) return null;
    const statusMap: Record<string, { label: string; color: string }> = {
      uploading: { label: 'アップロード中', color: 'default' },
      stored: { label: '待機中', color: 'blue' },
      transcribing: { label: '処理中', color: 'gold' },
      transcribed: { label: '完了', color: 'green' },
      failed: { label: '失敗', color: 'red' },
    };
    const setting = statusMap[status] || { label: status, color: 'default' };
    return <Tag color={setting.color}>{setting.label}</Tag>;
  }, []);

  const formatDateTimeLabel = (value?: string | null) => {
    return formatJstDateTime(value);
  };

  const getAuthToken = useCallback(async (): Promise<string> => {
    const { fetchAuthSession } = await import('../auth/gateway');
    const session = await fetchAuthSession();
    const token = session.tokens?.accessToken?.toString();
    if (!token) {
      throw new Error('No auth token available');
    }
    return token;
  }, []);

  const loadData = useCallback(async () => {
    if (!audioId || !selectedDevice) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      const detailResp = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!detailResp.ok) {
        throw new Error(`Audio detail fetch failed (${detailResp.status})`);
      }
      const audioJson = await detailResp.json();
      setAudioData(audioJson);

      const transcriptsResp = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}/transcripts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!transcriptsResp.ok) {
        secureLogger.error('Failed to fetch transcripts for detail view', { audioId, status: transcriptsResp.status });
        setTranscriptEntry(null);
      } else {
        const transcriptsJson: TranscriptListResponse = await transcriptsResp.json();
        const preferred = pickPreferredTranscript(transcriptsJson.transcripts || []);
        if (preferred) {
          const detailResp2 = await fetch(
            `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}/transcripts/${preferred.id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );
          let detailData: TranscriptDetail | undefined;
          if (detailResp2.ok) {
            detailData = await detailResp2.json();
          } else {
            detailData = {
              ...preferred,
              segments: [],
            } as TranscriptDetail;
          }
          setTranscriptEntry({ summary: preferred, detail: detailData });
        } else {
          setTranscriptEntry(null);
        }
      }
    } catch (err) {
      secureLogger.error('Audio detail load failed', { audioId, error: err });
      setError('音声データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [audioId, selectedDevice, getAuthToken]);

  const loadPersons = useCallback(async () => {
    setPersonListLoading(true);
    try {
      const token = await getAuthToken();
      const resp = await fetch(`${API_CONFIG.BASE_URL}/api/v1/persons?include_shared=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        throw new Error(`Person list fetch failed (${resp.status})`);
      }
      const json = await resp.json();
      setPersonList(Array.isArray(json?.persons) ? (json.persons as PersonSummary[]) : []);
    } catch (err) {
      secureLogger.error('Failed to load persons list', { error: err });
      message.error('人物リストの取得に失敗しました');
    } finally {
      setPersonListLoading(false);
    }
  }, [getAuthToken]);

  const loadEnrollmentSegments = useCallback(async () => {
    if (!audioId || !selectedDevice) {
      setEnrollmentSegments([]);
      return;
    }
    setEnrollmentLoading(true);
    try {
      const token = await getAuthToken();
      const resp = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}/speaker-enrollment-segments`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) {
        throw new Error(`Enrollment segments fetch failed (${resp.status})`);
      }
      const json = await resp.json();
      setEnrollmentSegments(Array.isArray(json?.segments) ? (json.segments as AudioEnrollmentSegmentState[]) : []);
    } catch (err) {
      secureLogger.error('Failed to load audio enrollment segments', { audioId, error: err });
      message.error('エンロール候補の取得に失敗しました');
      setEnrollmentSegments([]);
    } finally {
      setEnrollmentLoading(false);
    }
  }, [audioId, selectedDevice, getAuthToken]);

  const retryWhisperBatch = useCallback(async () => {
    if (!audioId || !selectedDevice || !audioData) {
      return;
    }
    setRetryingWhisper(true);
    try {
      const token = await getAuthToken();
      const resp = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/audio/${audioId}/whisper/retry`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!resp.ok) {
        throw new Error(`Retry failed (${resp.status})`);
      }
      message.success('音声認識バッチを再実行しました');
      await loadData();
      await loadEnrollmentSegments();
    } catch (err) {
      secureLogger.error('Whisper retry failed', { audioId, error: err });
      message.error('音声認識バッチの再実行に失敗しました');
    } finally {
      setRetryingWhisper(false);
    }
  }, [audioId, selectedDevice, audioData, getAuthToken, loadData, loadEnrollmentSegments]);

  const confirmRetryWhisper = useCallback(() => {
    Modal.confirm({
      title: '音声認識を再実行しますか？',
      content: '既存の認識結果は削除され、再度Whisperバッチが実行されます。',
      okText: '再実行',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: retryWhisperBatch,
    });
  }, [retryWhisperBatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPersons();
  }, [loadPersons]);

  useEffect(() => {
    loadEnrollmentSegments();
  }, [loadEnrollmentSegments]);

  useEffect(() => {
    setPendingInitialSeek(initialSeekTimestamp ?? null);
  }, [initialSeekTimestamp, audioId]);

  const summary = useMemo(() => {
    if (!transcriptEntry?.detail && !transcriptEntry?.summary) {
      return undefined;
    }
    return (
      getWhisperSegmentSummary(transcriptEntry?.detail) ||
      getWhisperSegmentSummary(transcriptEntry?.summary)
    );
  }, [transcriptEntry]);

  const normalizedSegments = useMemo(() => {
    if (!transcriptEntry?.detail || !audioData) {
      return [];
    }
    return buildSegmentsForAudio(transcriptEntry.detail, audioData.id);
  }, [transcriptEntry, audioData]);

  const enrollmentLookup = useMemo(() => {
    const map = new Map<string, AudioEnrollmentSegmentState>();
    enrollmentSegments.forEach((entry) => {
      const key = buildLookupKey(
        typeof entry.transcript_segment_id === 'number' ? entry.transcript_segment_id : undefined,
        entry.start_ms,
        entry.end_ms,
      );
      if (key) {
        map.set(key, entry);
      }
    });
    return map;
  }, [enrollmentSegments]);

  const [activeWordIndex, setActiveWordIndex] = useState<Record<string, number>>({});
  const [seekRequests, setSeekRequests] = useState<Record<string, { timeSec: number; play?: boolean; token: number }>>({});

  useEffect(() => {
    setActiveWordIndex({});
    setSeekRequests({});
  }, [audioId, transcriptEntry]);

  useEffect(() => {
    if (!pendingInitialSeek || !audioData || normalizedSegments.length === 0) {
      return;
    }
    const audioStart = parseTimestamp(audioData.start_time);
    const target = parseTimestamp(pendingInitialSeek);
    if (!audioStart || !target) {
      setPendingInitialSeek(null);
      return;
    }
    const targetSec = (target.getTime() - audioStart.getTime()) / 1000;
    if (!Number.isFinite(targetSec)) {
      setPendingInitialSeek(null);
      return;
    }
    const targetSegment =
      normalizedSegments.find(
        (segment) => targetSec >= segment.startSec && targetSec <= segment.endSec,
      ) || normalizedSegments[0];
    if (!targetSegment) {
      setPendingInitialSeek(null);
      return;
    }
    const relative = Math.max(0, targetSec - targetSegment.startSec);
    setSeekRequests((prev) => ({
      ...prev,
      [targetSegment.key]: {
        timeSec: relative,
        play: true,
        token: Date.now(),
      },
    }));
    setPendingInitialSeek(null);
  }, [pendingInitialSeek, audioData, normalizedSegments]);

  useEffect(() => {
    if (assignModalVisible && !selectedPersonId && personList.length > 0) {
      setSelectedPersonId(personList[0].id);
    }
  }, [assignModalVisible, personList, selectedPersonId]);

  const handleSegmentProgress = useCallback(
    (segmentKey: string, words: NormalizedWord[] | undefined, relativeTime: number) => {
      if (!words?.length) {
        return;
      }
      const idx = words.findIndex((word) => relativeTime >= word.startRelSec && relativeTime < word.endRelSec);
      setActiveWordIndex((prev) => {
        if (prev[segmentKey] === idx) {
          return prev;
        }
        return { ...prev, [segmentKey]: idx };
      });
    },
    [],
  );

  const handleWordClick = useCallback((segmentKey: string, word: NormalizedWord, index: number) => {
    setActiveWordIndex((prev) => ({ ...prev, [segmentKey]: index }));
    setSeekRequests((prev) => ({
      ...prev,
      [segmentKey]: {
        timeSec: word.startRelSec,
        play: true,
        token: Date.now(),
      },
    }));
  }, []);

  const openAssignModal = useCallback((segment: NormalizedSegment) => {
    setAssignTargetSegment(segment);
    setAssignNotes('');
    setSelectedPersonId((prev) => prev || personList[0]?.id);
    setAssignModalVisible(true);
  }, [personList]);

  const closeAssignModal = useCallback(() => {
    setAssignModalVisible(false);
    setAssignTargetSegment(null);
    setAssignNotes('');
    setAssigningKey(null);
  }, []);

  const handleAssignSubmit = useCallback(async () => {
    if (!assignTargetSegment || !audioId) {
      message.warning('セグメント情報が取得できませんでした');
      return;
    }
    if (!selectedPersonId) {
      message.warning('人物を選択してください');
      return;
    }
    try {
      setAssigningKey(assignTargetSegment.lookupKey);
      const token = await getAuthToken();
      const payload = {
        audio_id: audioId,
        transcript_segment_id: assignTargetSegment.transcriptSegmentId ?? null,
        start_ms: Math.round(assignTargetSegment.startMs),
        end_ms: Math.round(assignTargetSegment.endMs),
        notes: assignNotes.trim() ? assignNotes.trim() : undefined,
      };
      const resp = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/persons/${selectedPersonId}/speaker-enrollments`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        },
      );
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail?.detail || 'エンロール候補の追加に失敗しました');
      }
      message.success('エンロール候補に追加しました');
      setAssignModalVisible(false);
      setAssignTargetSegment(null);
      setAssignNotes('');
      await loadEnrollmentSegments();
    } catch (err) {
      secureLogger.error('Failed to create speaker enrollment', { audioId, error: err });
      message.error(err instanceof Error ? err.message : 'エンロール候補の追加に失敗しました');
    } finally {
      setAssigningKey(null);
    }
  }, [
    assignTargetSegment,
    audioId,
    selectedPersonId,
    assignNotes,
    getAuthToken,
    loadEnrollmentSegments,
  ]);

  const handleRemoveEnrollment = useCallback(
    async (entry: AudioEnrollmentSegmentState) => {
      try {
        setRemovingEnrollmentId(entry.id);
        const token = await getAuthToken();
        const resp = await fetch(
          `${API_CONFIG.BASE_URL}/api/v1/persons/${entry.person_id}/speaker-enrollments/${entry.id}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!resp.ok) {
          const detail = await resp.json().catch(() => ({}));
          throw new Error(detail?.detail || '候補の削除に失敗しました');
        }
        message.success('エンロール候補を削除しました');
        await loadEnrollmentSegments();
      } catch (err) {
        secureLogger.error('Failed to delete speaker enrollment segment', { audioId, error: err });
        message.error(err instanceof Error ? err.message : '候補の削除に失敗しました');
      } finally {
        setRemovingEnrollmentId(null);
      }
    },
    [audioId, getAuthToken, loadEnrollmentSegments],
  );

  if (!audioId) {
    return (
      <Card>
        <Empty description="音声IDが指定されていません" />
      </Card>
    );
  }

  if (!selectedDevice) {
    return (
      <Card>
        <Empty description="デバイスを選択してください" />
      </Card>
    );
  }

  const assignButtonDisabled = personListLoading || personList.length === 0;
  const listPageUrl = selectedDevice ? `/devices/${selectedDevice.id}/audio` : '/audio';
  const retryDisabled = !audioData || ['stored', 'transcribing'].includes(audioData.status);
  const recognitionProcessedAt =
    transcriptEntry?.detail?.created_at ?? transcriptEntry?.summary?.created_at ?? null;

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              音声詳細
            </Title>
            {audioId && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ID: {audioId}
              </Text>
            )}
          </div>
          <Space>
            {!embedded && (
              <>
                <Link to={listPageUrl}>
                  <Button>一覧に戻る</Button>
                </Link>
              </>
            )}
            <Button
              type="primary"
              danger
              onClick={confirmRetryWhisper}
              loading={retryingWhisper}
              disabled={retryDisabled}
            >
              音声認識を再実行
            </Button>
          </Space>
        </Space>

        {error && <Alert type="error" message={error} />}

        {loading && (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        )}

        {!loading && audioData && (
          <div>
            <Space wrap size="small" align="center">
              <Text type="secondary" style={{ fontSize: 12 }}>
                記録: {formatDateTimeLabel(audioData.start_time)}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                長さ: {(audioData.duration_ms / 1000).toFixed(1)}秒
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                形式: {audioData.codec.toUpperCase()}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                音声認識: {formatDateTimeLabel(recognitionProcessedAt)}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                サイズ: {(audioData.file_size / 1024).toFixed(1)} KB
              </Text>
              {renderStatusTag(audioData.status)}
            </Space>
          </div>
        )}

        {!loading && audioData && (
          <div>
            {transcriptEntry ? (
              (() => {
                const detail = transcriptEntry.detail;
                const isWhisper = isWhisperBatchEngine(detail?.engine);
                if (isWhisper && detail) {
                  const summaryParts: string[] = [];
                  if (summary?.segment_count) {
                    summaryParts.push(`セグメント: ${summary.segment_count}件`);
                  }
                  if (summary?.duration_ms) {
                    summaryParts.push(`合計 ${(summary.duration_ms / 1000).toFixed(1)}秒`);
                  }
                  if (summary?.language) {
                    summaryParts.push(`言語: ${summary.language}`);
                  }
                if (summary?.rtf) {
                  summaryParts.push(`RTF: ${summary.rtf}`);
                }
                if (isNoSpeechDetected(detail) || isNoSpeechDetected(transcriptEntry.summary)) {
                  summaryParts.push('認識区間なし');
                }

                  return (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Text>{hasRecognizedSpeech(detail) ? detail.transcript_text : getWhisperTranscriptMessage(detail)}</Text>
                      {summaryParts.length > 0 && (
                        <Text type="secondary">{summaryParts.join(' / ')}</Text>
                      )}
                      {enrollmentLoading && (
                        <Text type="secondary">エンロール候補を読み込んでいます...</Text>
                      )}
                      {normalizedSegments.length > 0 ? (
                        normalizedSegments.map((segment) => {
                          const assigned = segment.lookupKey ? enrollmentLookup.get(segment.lookupKey) : undefined;
                          const activeIdx = activeWordIndex[segment.key];
                          return (
                            <div
                              key={segment.key}
                              style={{
                                border: '1px dashed #d9d9d9',
                                borderRadius: 8,
                                padding: 12,
                                background: '#fafafa',
                              }}
                            >
                              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                  <Space wrap align="center">
                                    <Tag color="purple">{segment.speaker ?? 'Unknown'}</Tag>
                                    <Text strong>
                                      {formatTime(segment.startSec)} - {formatTime(segment.endSec)}
                                    </Text>
                                    {segment.speakerConf && (
                                      <Text type="secondary">
                                        conf {(segment.speakerConf * 100).toFixed(1)}%
                                      </Text>
                                    )}
                                  </Space>
                                  {!assigned && (
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={() => openAssignModal(segment)}
                                      disabled={assignButtonDisabled}
                                      loading={assigningKey === segment.lookupKey}
                                    >
                                      エンロール候補に追加
                                    </Button>
                                  )}
                                </div>
                                {assigned ? (
                                  <Space align="center" wrap>
                                    <Tag color="green">
                                      {assigned.person_display_name || '未命名の人物'}
                                    </Tag>
                                    {(() => {
                                      const stateInfo = describeEnrollmentState(assigned.enrollment_state);
                                      return <Tag color={stateInfo.color}>{stateInfo.label}</Tag>;
                                    })()}
                                    <Tooltip title="この区間をエンロール候補から外します">
                                      <Button
                                        size="small"
                                        danger
                                        loading={removingEnrollmentId === assigned.id}
                                        onClick={() => handleRemoveEnrollment(assigned)}
                                      >
                                        候補から削除
                                      </Button>
                                    </Tooltip>
                                  </Space>
                                ) : (
                                  assignButtonDisabled && !personListLoading && personList.length === 0 && (
                                    <Text type="secondary">まず人物を登録してください</Text>
                                  )
                                )}
                                <Text>{segment.text || '...'}</Text>
                                <SegmentAudioPlayer
                                  audioId={audioData.id}
                                  deviceId={selectedDevice.id}
                                  startSec={segment.startSec}
                                  endSec={segment.endSec}
                                  label="セグメント音声"
                                  onProgress={(relative) =>
                                    handleSegmentProgress(segment.key, segment.words, relative)
                                  }
                                  seekRequest={seekRequests[segment.key] || null}
                                />
                                {segment.words && segment.words.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                    {segment.words.map((word, idx) => (
                                      <Button
                                        key={word.key}
                                        size="small"
                                        type={activeIdx === idx ? 'primary' : 'default'}
                                        onClick={() => handleWordClick(segment.key, word, idx)}
                                      >
                                        {word.text}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </Space>
                            </div>
                          );
                        })
                      ) : (
                        <Text type="secondary">この音声に対応するセグメントはありません</Text>
                      )}
                      {detail.audio_ids?.length > 1 && (
                        <Text type="secondary">
                          まとめ対象: {detail.audio_ids.join(', ')}
                        </Text>
                      )}
                    </Space>
                  );
                }

                return (
                  <AudioTranscriptionEditor
                    audioId={audioData.id}
                    deviceId={selectedDevice.id}
                    transcription={detail?.transcript_text || audioData.transcription || ''}
                    confidence={detail?.confidence ?? audioData.confidence}
                    onUpdate={(newText) => {
                      setTranscriptEntry((prev) =>
                        prev
                          ? {
                              summary: prev.summary ? { ...prev.summary, transcript_text: newText } : prev.summary,
                              detail: prev.detail ? { ...prev.detail, transcript_text: newText } : prev.detail,
                            }
                          : prev,
                      );
                      setAudioData((prev) => (prev ? { ...prev, transcription: newText } : prev));
                    }}
                  />
                );
              })()
            ) : (
              <Empty description="音声認識結果はまだありません" />
            )}
          </div>
        )}
      </Space>
      <Modal
        title="エンロール候補に追加"
        open={assignModalVisible}
        onCancel={closeAssignModal}
        okText="追加する"
        cancelText="キャンセル"
        okButtonProps={{
          disabled: !assignTargetSegment || !selectedPersonId,
          loading: Boolean(assigningKey),
        }}
        onOk={handleAssignSubmit}
      >
        {assignTargetSegment ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              {(() => {
                const durationSec = Math.max(0, assignTargetSegment.endSec - assignTargetSegment.startSec);
                return (
                  <>
                    <Text>対象区間: </Text>
                    <Text strong>
                      {formatTime(assignTargetSegment.startSec)} - {formatTime(assignTargetSegment.endSec)}
                    </Text>
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      ({durationSec.toFixed(1)}秒)
                    </Text>
                  </>
                );
              })()}
            </div>
            <div>
              <Text>人物</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="人物を選択"
                loading={personListLoading}
                value={selectedPersonId}
                onChange={(value) => setSelectedPersonId(value)}
                options={personList.map((person) => ({
                  label: person.display_name,
                  value: person.id,
                }))}
              />
            </div>
            <div>
              <Text>メモ（任意）</Text>
              <Input.TextArea
                rows={3}
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder="この区間を選んだ理由などをメモできます"
              />
            </div>
          </Space>
        ) : (
          <Spin />
        )}
      </Modal>
    </Card>
  );
};

export default AudioDetailPage;
